import { StrictMode, useState, useEffect } from "react";
import Snowfall from "react-snowfall";
import { Spinner } from "@/components/ui/spinner";
import { createRoot } from "react-dom/client";
import "./index.css";
import "./lib/setup-fetch-interceptor";

import { PostHogProvider } from "posthog-js/react";
import { ThemeProvider } from "@/components/theme-provider";
import { applySavedAppearance } from "./components/nav_components/appearance-settings.tsx";
import { ClerkProvider, useAuth } from "@clerk/react";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
  useLocation,
  type RouteObject,
} from "react-router-dom";
import {
  CheckCircle2Icon,
  CircleXIcon,
  InfoIcon,
  TriangleAlertIcon,
} from "lucide-react";
import RouteErrorBoundary from "./components/RouteErrorBoundary.tsx";
import { AppLoading, watchSplash } from "./components/app-loading.tsx";
import ErrorBoundary from "./components/ErrorBoundary.tsx";


import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useAppVersionCheck } from "./hooks/use-app-version-check";


import {
  useOnboardingComplete,
  usePaywallComplete,
} from "./hooks/onboarding.ts";
import { useComplianceRole } from "./hooks/IsAdminContext.tsx";
import { MailPermissionGuard } from "./hooks/MailPermissionGuard.tsx";
import posthog from "posthog-js";
import { usePostHogSync } from "./hooks/PostHogSync.tsx";
import { AmyEasterEgg } from "./components/AmyEasterEgg.tsx";

// Every page, loaded with the app (no route waits on a download).
import SharePage from "./pages/SharePage.tsx";
import LogIn from "./pages/LogIn.tsx";
import SignUpPage from "./pages/SignUp.tsx";
import SSOCallback from "./pages/SSOCallback.tsx";
import Onboarding from "./pages/Onboarding.tsx";
import TestPage from "./pages/TestPage.tsx";
import StripePage from "./pages/Stripe.tsx";
import Pricing from "./pages/Pricing.tsx";
import { DashboardWithUsageLimit } from "./pages/DashboardDataProvider.tsx";
import TasksDashboard from "./components/dashboard_home/TasksDashboard.tsx";
import AmyChat from "./pages/AmyChat.tsx";
import Campaigns from "./components/dashboard_campaign/Campaigns.tsx";
import GlobalCrmPage from "./components/dashboard_crm/GlobalCrmPage.tsx";
import CampaignPage from "./components/dashboard_campaign/CampaignPage.tsx";
import CreatorsTab from "./components/dashboard_campaign/tabs/CreatorsTab.tsx";
import InboxTab from "./components/dashboard_campaign/tabs/InboxTab.tsx";
import BriefTab from "./components/dashboard_campaign/tabs/BriefTab.tsx";
import DraftsTab from "./components/dashboard_campaign/tabs/DraftsTab.tsx";
import AnalyticsTab from "./components/dashboard_campaign/analytics/AnalyticsTab.tsx";
import StagesTab from "./components/dashboard_campaign/tabs/StagesTab.tsx";
import SettingsTab from "./components/dashboard_campaign/tabs/SettingsTab.tsx";
import CampaignForm from "./components/dashboard_create_campaign/CampaignForm.tsx";
import SettingsPage from "./components/dashboard_settings/SettingsPage.tsx";
import Manager from "./pages/Manager.tsx";
import SuccessPage from "./components/dashboard_success/SuccessPage.tsx";
import BatchStatusPage from "./components/dashboard_sourcing/BatchStatusPage.tsx";
import ActivityPage from "./components/dashboard_activity/ActivityPage.tsx";
import NotFound from "./pages/NotFound.tsx";

const AppVersionCheck = () => {
  useAppVersionCheck();
  return null;
};

// Christmas snowfall component
const ChristmasSnowfall = () => {
  const [isEnabled, setIsEnabled] = useState(() => {
    return localStorage.getItem("christmas_mode_enabled") === "enabled";
  });

  useEffect(() => {
    const handleToggle = (e: CustomEvent<string>) => {
      setIsEnabled(e.detail === "enabled");
    };

    window.addEventListener("christmas-mode-toggle", handleToggle as EventListener);
    return () => {
      window.removeEventListener("christmas-mode-toggle", handleToggle as EventListener);
    };
  }, []);

  if (!isEnabled) return null;

  return (
    <Snowfall
      style={{
        position: "fixed",
        width: "100vw",
        height: "100vh",
        zIndex: 9999,
        pointerEvents: "none",
      }}
      snowflakeCount={100}
    />
  );
};

// Protected route wrapper
const ProtectedRoute = () => {
  const { isSignedIn, isLoaded } = useAuth();
  const location = useLocation();

  usePostHogSync();

  // These should be hooks that return the current state
  const { onboardingComplete, isLoaded: onboardingLoaded } =
    useOnboardingComplete();
  const { paywallComplete, isLoaded: paywallLoaded } = usePaywallComplete();
  const { isCompliance, isLoaded: complianceLoaded } = useComplianceRole();

  if (!isLoaded && onboardingLoaded) {
    return <AppLoading />;
  }

  if (!isSignedIn && isLoaded) {
    return <Navigate to="/login" replace />;
  }

  const currentPath = location.pathname;
  // console.log("paywallComplete", paywallComplete);
  // If onboarding is not complete and the user is not on the onboarding page, redirect to onboarding
  if (
    (!onboardingComplete || !paywallComplete) &&
    currentPath !== "/onboarding" &&
    onboardingLoaded &&
    paywallLoaded
  ) {
    return <Navigate to="/onboarding" replace />;
  }

  // Compliance reviewers get exactly one surface: the Tasks page. Wait for
  // the membership to load before rendering anything, so they never flash
  // the full dashboard; skip the mailbox wall — reviewers don't send email.
  if (isSignedIn && !complianceLoaded) {
    return <AppLoading />;
  }
  if (isCompliance) {
    if (!currentPath.startsWith("/dashboard/tasks")) {
      return <Navigate to="/dashboard/tasks" replace />;
    }
    return <Outlet />;
  }

  return (
    <MailPermissionGuard>
      <Outlet />
    </MailPermissionGuard>
  );
};

const OnboardingPaywallRoute = () => {
  const { isSignedIn, isLoaded } = useAuth();
  const { onboardingComplete, isLoaded: onboardingLoaded } =
    useOnboardingComplete();
  const { paywallComplete, isLoaded: paywallLoaded } = usePaywallComplete();

  if (!isLoaded && paywallLoaded && onboardingLoaded) {
    return <AppLoading />;
  }

  if (!isSignedIn) {
    return <Navigate to="/login" replace />;
  }

  if (
    onboardingComplete &&
    paywallComplete &&
    window.location.pathname === "/onboarding"
  ) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
};

// Public only route wrapper (for login/signup pages)
const PublicOnlyRoute = () => {
  const { isSignedIn, isLoaded } = useAuth();

  if (!isLoaded) {
    return <AppLoading />;
  }

  if (isSignedIn && isLoaded) {
    return <Navigate to="/dashboard/campaigns" replace />;
  }

  return <Outlet />;
};

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const POSTHOG_KEY = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;

if (!PUBLISHABLE_KEY) {
  throw new Error("Add your Clerk Publishable Key to the .env file");
}

// App routes, with authentication. Every page is in the one bundle the
// splash (index.html) covers, so navigating never waits on a download.
const appRoutes: RouteObject[] = [
  // Public routes (accessible to all)
  {
    path: "/",
    element: <Navigate to="/login" replace />,
    errorElement: <RouteErrorBoundary />,
  },

  // Public, login-less campaign share link (also served at share.uplodio.com root).
  {
    path: "/share/:token",
    Component: SharePage,
    errorElement: <RouteErrorBoundary />,
  },

  // Public only routes (only when NOT logged in)
  {
    element: <PublicOnlyRoute />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: "/login",
        Component: LogIn,
      },
      {
        path: "/signup",
        Component: SignUpPage,
      },
    ],
  },

  // Google / Microsoft redirect back here. Outside PublicOnlyRoute: the
  // session turns active mid-callback and the callback does its own redirect.
  {
    path: "/sso-callback",
    Component: SSOCallback,
    errorElement: <RouteErrorBoundary />,
  },
  {
    element: <OnboardingPaywallRoute />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: "/onboarding",
        Component: Onboarding,
      },
    ],
  },

  // Protected routes (only when logged in)
  {
    element: <ProtectedRoute />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: "/test",
        Component: TestPage,
      },

      {
        path: "/stripe",
        children: [
          {
            path: "success",
            element: <StripePage operationtype="success" />,
          },
          {
            path: "cancel",
            element: <StripePage operationtype="cancel" />,
          },
        ],
      },

      {
        path: "/pricing",

        Component: Pricing,
      },
      {
        path: "/dashboard",
        Component: DashboardWithUsageLimit,
        children: [
          {
            index: true, // This makes it the default route
            element: <Navigate to="/dashboard/campaigns" replace />, // Redirects to /dashboard/campaigns
          },
          {
            path: "/dashboard/tasks",
            Component: TasksDashboard,
          },
          {
            path: "/dashboard/amy",
            children: [
              {
                index: true,
                element: <Navigate to="new" replace />,
              },
              {
                path: ":chatId",
                Component: AmyChat,
              },
            ],
          },
          {
            path: "/dashboard/campaigns",
            Component: Campaigns,
          },
          {
            // Org-wide creators table (every campaign, Campaign + Inbox columns).
            path: "/dashboard/crm",
            Component: GlobalCrmPage,
          },
          {
            // Moved to the staff /manager area; keep old links working.
            path: "/dashboard/batch-status",
            element: <Navigate to="/manager/batch-status" replace />,
          },
          {
            path: "/dashboard/success",
            element: <Navigate to="/manager/success" replace />,
          },

          {
            path: "/dashboard/campaigns/:campaign_id",
            Component: CampaignPage,
            children: [
              {
                index: true,
                element: <Navigate to="creators" replace />,
              },

              {
                path: "creators",
                Component: CreatorsTab,
              },
              {
                path: "inbox/:creator_id?",
                Component: InboxTab,
              },
              {
                path: "brief",
                children: [
                  {
                    index: true,
                    element: <Navigate to="brand_and_product" replace />,
                  },
                  {
                    path: ":section",
                    Component: BriefTab,
                  },
                ],
              },
              {
                path: "drafts/:creator_id?",
                Component: DraftsTab,
              },
              {
                path: "analytics/:creator_id?",
                Component: AnalyticsTab,
              },
              {
                path: "stages",
                children: [
                  {
                    index: true,
                    Component: StagesTab,
                  },
                  {
                    // Amy-v2 PipelineTab section param (overview | <phase key>)
                    // — legacy StagesTab ignores it.
                    path: ":phase",
                    Component: StagesTab,
                  },
                ],
              },
              {
                path: "settings",
                children: [
                  {
                    index: true,
                    element: <Navigate to="info" replace />,
                  },
                  {
                    path: ":section",
                    Component: SettingsTab,
                  },
                ],
              },
            ],
          },
          {
            path: "/dashboard/campaigns/create",
            Component: CampaignForm,
          },
          {
            path: "/dashboard/settings",
            children: [
              {
                index: true,
                element: <Navigate to="brand-setup" replace />, // default category
              },
              {
                path: ":category",
                Component: SettingsPage,
              },
            ],
          },
        ],
      },
      {
        // Staff-only area (Clerk uplodio_member; Manager redirects everyone
        // else to /dashboard). Switched to from the header's AreaSwitch.
        path: "/manager",
        Component: Manager,
        children: [
          {
            index: true,
            element: <Navigate to="/manager/success" replace />,
          },
          {
            // Success board — the Success controller's snapshots (amy/lib/ops).
            path: "/manager/success",
            Component: SuccessPage,
          },
          {
            // Sourcing monitor (raw_creators batches + analysis fleet).
            path: "/manager/batch-status",
            Component: BatchStatusPage,
          },
          {
            // CS MCP call log + client plan history (amy/lib/ops/activity.ts).
            path: "/manager/activity",
            Component: ActivityPage,
          },
        ],
      },
    ],
  },
  {
    path: "*",
    Component: NotFound,
    errorElement: <RouteErrorBoundary />,
  },
];

// On the share.* subdomain, the share token sits at the root path
// (share.uplodio.com/{token}). Use a dedicated, login-less router there.
const isShareHost =
  typeof window !== "undefined" &&
  window.location.hostname.startsWith("share.");

const shareRoutes: RouteObject[] = [
  {
    path: "/:token",
    Component: SharePage,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "*",
    Component: NotFound,
    errorElement: <RouteErrorBoundary />,
  },
];

// Only this host's router is created: the share router's "/:token" would
// match "/login".
const router = createBrowserRouter(isShareHost ? shareRoutes : appRoutes);
watchSplash(router);

posthog.init(POSTHOG_KEY, {
  api_host: POSTHOG_HOST,
  autocapture: true,
  capture_pageview: false,
});

declare global {
  interface Window {
    Clerk?: any;
  }
}

export { };

// Add this right after posthog.init() and before createRoot()
window.addEventListener("error", (event) => {
  if (event.message.includes("removeChild") || event.message.includes("Node")) {
    const errorDetails = {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      stack: event.error?.stack,
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      previousUrl: document.referrer,
      clerkLoaded: window.Clerk !== undefined,
      activeElement: document.activeElement?.tagName,
    };

    console.error("DOM manipulation error caught:", errorDetails);
    posthog.capture("dom_removechild_error", errorDetails);
    // event.preventDefault(); // Prevents error from showing in console
  }
});

// Force full reload on HMR to prevent duplicate ClerkProvider
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    window.location.reload();
  });
}

// Saved radius/primary-color before first paint — the header toggles whose
// mount effects used to apply these no longer render on dashboard pages.
applySavedAppearance();

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ErrorBoundary>
      <PostHogProvider client={posthog}>
        <ClerkProvider
          publishableKey={PUBLISHABLE_KEY}
          signInUrl="/login"
          signUpUrl="/signup"
          afterSignOutUrl="/"
          appearance={{
            cssLayerName: "clerk",
            variables: {
              colorPrimary: "var(--primary)",
              colorPrimaryForeground: "var(--primary-foreground)",
              colorDanger: "var(--destructive)",
              colorSuccess: "var(--success)",
              colorWarning: "var(--warning)",
              colorNeutral: "var(--foreground)",
              colorForeground: "var(--foreground)",
              colorMutedForeground: "var(--muted-foreground)",
              colorBackground: "var(--background)",
              colorInputForeground: "var(--foreground)",
              colorInput: "var(--background)",
              // borderRadius: "var(--radius)",
            },
          }}
        >
          <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
            <TooltipProvider delayDuration={0}>
            <RouterProvider router={router} />
            <AmyEasterEgg />
            <ChristmasSnowfall />
            <AppVersionCheck />
            <Toaster
              closeButton
              icons={{
                success: <CheckCircle2Icon className="size-4 text-success" />,
                info: <InfoIcon className="size-4 text-blue-500" />,
                warning: <TriangleAlertIcon className="size-4 text-warning" />,
                error: <CircleXIcon className="size-4 text-destructive" />,
                loading: <Spinner className="size-3.75 text-muted-foreground" />,
              }}
              toastOptions={{
                classNames: {
                  closeButton:
                    "!absolute !left-84.5 !top-2 !text-muted-foreground hover:!text-foreground !border-0 !rounded-[calc(var(--radius-lg)-1px)] !h-6 !w-6 ![&_svg]:size-4 !p-0.5",
                  description: "!text-muted-foreground !text-xs",
                },
                style: {
                  height: "auto",
                  padding: "13px 16px",
                  fontFamily: "var(--font-poppins)",
                  borderRadius: "var(--radius-lg)",
                },
              }}
            />
            </TooltipProvider>
          </ThemeProvider>
        </ClerkProvider>
      </PostHogProvider>
    </ErrorBoundary>
  </StrictMode>
);
